import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setClaimModeHeader } from '@edison/api-client-react';

// The R&D-value methodologies. Internal keys match the server (labour-calc.ts ClaimMode).
export type ClaimMode = 'business_hours' | 'business_hours_gross' | 'all_project_hours' | 'classified_rd' | 'manual_timesheet' | 'annual_salary_cap' | 'apportionment';

// UI labels + descriptions live here so they can change without touching the wire keys.
export const CLAIM_MODE_META: Record<ClaimMode, { label: string; description: string }> = {
  business_hours: { label: 'R&D hours · workday cap', description: 'Just the R&D part of each day, capped to ~1 workday per person (≤7.6h/weekday, weekends excluded)' },
  business_hours_gross: { label: 'All project hours · workday cap', description: 'All time on R&D projects, capped to ~1 workday per person (≤7.6h/weekday, weekends excluded)' },
  classified_rd: { label: 'R&D hours · no cap', description: 'Just the R&D part of every logged hour, no daily cap' },
  all_project_hours: { label: 'All project hours · no cap', description: 'Every logged hour on R&D projects, no daily cap' },
  manual_timesheet: { label: 'Manual timesheets', description: 'Hours entered by hand — every hour on an R&D project, taken as entered' },
  annual_salary_cap: { label: 'Annual salary ceiling', description: 'Every hour on R&D projects, capped at each person\'s paid hours for their employment period — at the ceiling their entire salary is included, never more' },
  apportionment: {
    label: 'Apportionment (adopted shares)',
    description: 'Each salaried person\'s loaded salary (base + super + payroll tax + insurances) × their effort weight per project — spread by WORK SESSIONS, not hours (FY2026 durations are stitching-inflated and unreliable; hours are shown for reference only) — × the project\'s ADOPTED claimable R&D share, prorated to their employment period by calendar month. Casual staff are priced on capped R&D hours × hourly rate instead. Both pending and approved sessions count toward the weight. Shares are derived from per-commit classification counts (a commit-count weighting, not hours) and must be reviewed and adopted by a person before this mode can compute. Requires every effort-bearing project to have an adopted share.',
  },
};
export const CLAIM_MODES: ClaimMode[] = ['business_hours', 'business_hours_gross', 'classified_rd', 'all_project_hours', 'manual_timesheet', 'annual_salary_cap', 'apportionment'];

interface ClaimModeState {
  mode: ClaimMode;
  setMode: (mode: ClaimMode) => void;
}

// Global "lens": the active mode is persisted per browser and pushed onto every API request via
// the X-Claim-Mode header, so every R&D figure in the app is computed the same way. Locked claims
// ignore it server-side (they stay frozen at their lock-time mode).
export const useClaimModeStore = create<ClaimModeState>()(
  persist(
    (set) => ({
      mode: 'business_hours',
      setMode: (mode) => { setClaimModeHeader(mode); set({ mode }); },
    }),
    {
      name: 'edison-claim-mode',
      onRehydrateStorage: () => (state) => {
        // Sanitize: drop persisted keys that no longer exist in CLAIM_MODE_META (e.g. old 'manual_timesheets').
        const safe: ClaimMode = state?.mode && state.mode in CLAIM_MODE_META ? state.mode : 'business_hours';
        if (state && state.mode !== safe) state.mode = safe;
        setClaimModeHeader(safe);
      },
    },
  ),
);

// Ensure the header reflects the current (possibly-default) mode even before rehydration fires.
setClaimModeHeader(useClaimModeStore.getState().mode);
